import React from 'react';
import { Apple, Smartphone, ChevronDown, Sparkles, Heart, Star } from 'lucide-react';

export default function Hero() {
  return (
    <header className="relative overflow-hidden bg-gradient-to-br from-primary-100 via-secondary-50 to-accent-50">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 text-accent-400 animate-pulse">
        <Sparkles className="w-8 h-8" />
      </div>
      <div className="absolute top-40 right-20 text-violet-300 animate-bounce" style={{ animationDelay: '1s' }}>
        <Heart className="w-6 h-6" />
      </div>
      <div className="absolute bottom-40 left-20 text-secondary-400 animate-pulse" style={{ animationDelay: '2s' }}>
        <Star className="w-5 h-5" />
      </div>
      
      <nav className="container mx-auto px-6 py-4 relative z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <img
                src="/wezen-logo.png"
                alt="WeZen Logo"
                className="w-10 h-10 rounded-lg object-cover shadow-lg"
              />
              <div className="absolute -inset-1 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-lg blur opacity-30"></div>
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-primary-800 to-secondary-700 bg-clip-text text-transparent">WeZen</span>
          </div>
          {/* Ajout du lien Entreprise */}
          <div>
            <a
              href="/entreprise"
              className="ml-6 inline-block px-5 py-2 rounded-full bg-primary-500 text-white font-semibold shadow hover:bg-primary-600 transition-colors border border-primary-500 hover:border-primary-600"
            >
              Entreprise
            </a>
          </div>
        </div>
      </nav>

      <section className="relative">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-accent-400 to-violet-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-secondary-400 to-primary-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 pt-20 pb-24 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-secondary-100 px-4 py-2 rounded-full mb-6 border border-primary-200">
                <Sparkles className="w-4 h-4 text-secondary-600" />
                <span className="text-sm font-medium text-primary-700">Nouvelle version disponible</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-primary-800 via-accent-600 to-violet-600 bg-clip-text text-transparent">
                  Révolutionnez votre
                </span>
                <br />
                <span className="bg-gradient-to-r from-secondary-600 via-accent-500 to-violet-500 bg-clip-text text-transparent">
                  bien-être
                </span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-12 max-w-2xl leading-relaxed">
                Méditation, nutrition et motivation réunies dans une seule
                application pour <span className="font-semibold text-primary-700">transformer votre quotidien</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12">
                <a
                  href="https://apps.apple.com/fr/app/wezen/id6738658060"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-8 py-4 rounded-full hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Apple className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  Télécharger sur l'App Store
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.beewton.nutrizen"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center gap-2 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white px-8 py-4 rounded-full hover:from-secondary-600 hover:to-secondary-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <Smartphone className="w-6 h-6 group-hover:scale-110 transition-transform" />
                  Télécharger sur Google Play
                </a>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                </div>
                <span>4.8/5</span>
              </div>
            </div>
            
            <div className="flex-1 relative">
              <div className="relative z-10 transform hover:scale-105 transition duration-500">
                <img
                  src="/wezen-protfolio-mockup.png"
                  alt="WeZen App Interface"
                  className="w-full max-w-lg mx-auto rounded-[1.5rem] shadow-2xl"
                />
              </div>
              
              {/* Floating elements around mockup */}
              <div className="absolute -top-4 -left-4 bg-gradient-to-br from-accent-400 to-accent-500 p-3 rounded-2xl shadow-lg animate-float">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-gradient-to-br from-violet-300 to-violet-400 p-3 rounded-2xl shadow-lg animate-float" style={{ animationDelay: '1s' }}>
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              
              <div className="absolute inset-0 bg-gradient-to-tr from-accent-300/20 via-violet-300/20 to-secondary-400/20 blur-3xl -z-10 transform scale-95 animate-pulse"></div>
            </div>
          </div>
          
          <div className="text-center mt-16">
            <ChevronDown className="w-8 h-8 text-secondary-500 mx-auto animate-bounce" />
          </div>
        </div>
      </section>
    </header>
  );
}
